import React, { useReducer, useEffect, useMemo, useRef } from 'react';
import {
  CheckCircle2,
  Save,
  XCircle,
  Flag,
  Lock,
  User,
  Calendar
} from 'lucide-react';
import { StagingRecord, FieldSchema, ValidationRule, RecordData } from '../../types';
import { useValidation } from '../../hooks/useValidation';
import { SchemaField } from '../molecules/SchemaField';
import { setNestedValue } from '../../utils/nestedData';

interface ResolutionEditorProps {
  record: StagingRecord;
  schema: FieldSchema[];
  rules: ValidationRule[];
  onSave: (id: string, updatedData: RecordData, comment: string) => void;
  onReject: (id: string, reason: string) => void;
  readOnly?: boolean;
  displayName?: string;
  hideFooter?: boolean;
}

interface EditorState {
  formData: RecordData;
  overrides: Set<string>;
  rejectReason: string;
  importComment: string;
  isRejectMode: boolean;
}

type EditorAction =
  | { type: 'RESET'; payload: RecordData }
  | { type: 'UPDATE_FIELD'; path: string; value: any }
  | { type: 'TOGGLE_OVERRIDE'; key: string }
  | { type: 'SET_REJECT_REASON'; payload: string }
  | { type: 'SET_IMPORT_COMMENT'; payload: string }
  | { type: 'SET_REJECT_MODE'; payload: boolean };

function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case 'RESET':
      return {
        formData: action.payload,
        overrides: new Set(),
        rejectReason: '',
        importComment: '',
        isRejectMode: false,
      };
    case 'UPDATE_FIELD':
      return { ...state, formData: setNestedValue(state.formData, action.path, action.value) as RecordData };
    case 'TOGGLE_OVERRIDE': {
      const next = new Set(state.overrides);
      if (next.has(action.key)) {
        next.delete(action.key);
      } else {
        next.add(action.key);
      }
      return { ...state, overrides: next };
    }
    case 'SET_REJECT_REASON':
      return { ...state, rejectReason: action.payload };
    case 'SET_IMPORT_COMMENT':
      return { ...state, importComment: action.payload };
    case 'SET_REJECT_MODE':
      return { ...state, isRejectMode: action.payload };
    default:
      return state;
  }
}

export const ResolutionEditor: React.FC<ResolutionEditorProps> = ({
  record,
  schema,
  rules,
  onSave,
  onReject,
  readOnly = false,
  displayName,
  hideFooter = false,
}) => {
  const [state, dispatch] = useReducer(editorReducer, {
    formData: record.data,
    overrides: new Set<string>(),
    rejectReason: '',
    importComment: '',
    isRejectMode: false,
  });
  const { formData, overrides, rejectReason, importComment, isRejectMode } = state;

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Reset state when record changes
  useEffect(() => {
    dispatch({ type: 'RESET', payload: record.data });
  }, [record]);

  // Validation Logic: merge client-side rules with server-side fieldErrors from the database.
  // Server errors are shown for fields whose value hasn't been changed by the user;
  // once a field is edited, only client-side validation applies to that field.
  const clientErrors = useValidation(formData, rules);
  const validationErrors = useMemo(() => {
    const merged = { ...clientErrors };
    const serverFieldErrors = record.fieldErrors;
    if (serverFieldErrors) {
      for (const [field, message] of Object.entries(serverFieldErrors)) {
        if (!merged[field]) {
          const originalValue = record.originalData?.[field] ?? record.data[field];
          if (formData[field] === originalValue) {
            merged[field] = message;
          }
        }
      }
    }
    return merged;
  }, [clientErrors, record, formData]);

  const activeErrors = Object.keys(validationErrors).filter(key => !overrides.has(key));
  const hasActiveErrors = activeErrors.length > 0;
  const isReady = !hasActiveErrors;

  // Handlers
  const handleFieldChange = (path: string, value: any) => {
    if (readOnly) return;
    dispatch({ type: 'UPDATE_FIELD', path, value });
  };

  const toggleOverride = (key: string) => {
    if (readOnly) return;
    dispatch({ type: 'TOGGLE_OVERRIDE', key });
  };

  const handleSaveClick = () => {
    if (readOnly) return;
    onSave(record.id, formData, importComment);
  };

  const handleRejectClick = () => {
    if (readOnly) return;
    dispatch({ type: 'SET_REJECT_MODE', payload: true });
    requestAnimationFrame(() => textareaRef.current?.focus());
  };

  const confirmReject = () => {
    if (!rejectReason.trim()) return;
    onReject(record.id, rejectReason);
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">

      {/* HEADER */}
      <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
        <div>
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            {displayName || 'Record'} <span className="font-mono text-slate-500 text-sm">#{record.id}</span>
            {readOnly && <Lock className="w-4 h-4 text-slate-400" />}
          </h2>
          <p className="text-xs text-slate-400 mt-1 font-mono">Imported on {record.importDate}</p>
        </div>
        <div>
          {record.status === 'imported' ? (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-teal-50 text-teal-700 border border-teal-200">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
              Imported
            </span>
          ) : record.status === 'rejected' ? (
             <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
              <XCircle className="w-3.5 h-3.5 mr-1.5" />
              Rejected
            </span>
          ) : !readOnly && isReady ? (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
              Ready to Import
            </span>
          ) : !readOnly ? (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
              <Flag className="w-3.5 h-3.5 mr-1.5" />
              Needs Attention
            </span>
          ) : null}
        </div>
      </div>

      {/* BODY - FORM */}
      <div className="flex-1 overflow-y-auto p-6 relative bg-white">
        {readOnly && (
            <div className="absolute top-0 left-0 right-0 z-10 pointer-events-none h-4 bg-gradient-to-b from-white to-transparent"></div>
        )}
        <div className="max-w-2xl mx-auto">
          {schema.map(field => (
            <SchemaField
              key={field.key}
              field={field}
              path={field.key}
              value={formData[field.key]}
              onChange={handleFieldChange}
              errors={validationErrors}
              overrides={overrides}
              onToggleOverride={toggleOverride}
              readOnly={readOnly}
            />
          ))}
        </div>
      </div>

      {/* FOOTER - ACTIONS */}
      {!hideFooter && (
      <div className={`px-6 py-4 border-t transition-colors duration-300 ${
        readOnly ? 'bg-slate-50 border-slate-200' :
        hasActiveErrors ? 'bg-red-50/30 border-red-100' : 'bg-slate-50 border-slate-200'
      }`}>
        {readOnly ? (
           <div className="flex flex-col space-y-3">
             <div className="flex items-start">
                <div className={`p-2 rounded-full mr-3 shrink-0 ${record.status === 'imported' ? 'bg-teal-100' : 'bg-red-100'}`}>
                  {record.status === 'imported' ? (
                     <CheckCircle2 className="w-5 h-5 text-teal-600" />
                  ) : (
                     <XCircle className="w-5 h-5 text-red-600" />
                  )}
                </div>

                <div className="flex-1">
                   <h3 className="text-sm font-bold text-slate-900">
                     {record.status === 'imported' ? 'Record Imported' : 'Record Rejected'}
                   </h3>
                   <div className="text-xs text-slate-500 mt-1 flex items-center flex-wrap gap-1">
                     {record.resolvedBy === 'System' || !record.resolvedBy ? (
                        <span className="bg-slate-200 px-1.5 py-0.5 rounded text-slate-700 font-medium text-[10px] uppercase tracking-wide">Auto-processed</span>
                     ) : (
                        <span className="flex items-center">
                          <User className="w-3 h-3 mr-1" />
                          Action by <span className="font-medium text-slate-700 ml-1">{record.resolvedBy}</span>
                        </span>
                     )}
                     {record.resolutionDate && (
                       <span className="flex items-center text-slate-400 ml-2 border-l border-slate-300 pl-2">
                         <Calendar className="w-3 h-3 mr-1" />
                         <span className="font-mono">{new Date(record.resolutionDate).toLocaleString()}</span>
                       </span>
                     )}
                   </div>
                </div>
             </div>

             {record.resolutionNote ? (
               <div className="ml-12 bg-white border border-slate-200 rounded-lg p-3 text-sm text-slate-600 shadow-sm relative">
                 <div className="absolute top-0 left-4 -mt-1.5 w-3 h-3 bg-white border-t border-l border-slate-200 transform rotate-45"></div>
                 <span className="font-bold text-[10px] text-slate-400 uppercase tracking-wider block mb-1">
                   {record.status === 'imported' ? 'Import Note' : 'Rejection Reason'}
                 </span>
                 {record.resolutionNote}
               </div>
             ) : record.status === 'imported' && (record.resolvedBy === 'System' || !record.resolvedBy) ? (
                 <div className="ml-12 text-xs text-slate-400 italic">
                    This record passed all validation checks and was automatically imported.
                 </div>
             ) : null}
           </div>
        ) : hasActiveErrors ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center text-red-700 font-medium text-sm">
              <XCircle className="w-5 h-5 mr-2" />
              {activeErrors.length} Unresolved Issue{activeErrors.length !== 1 && 's'}
            </div>
            <button
              onClick={handleRejectClick}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium shadow-sm transition-colors focus:ring-2 focus:ring-offset-2 focus:ring-red-500 text-sm"
            >
              Reject Record
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label htmlFor="import-comment" className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Audit Note / Import Comment</label>
              <input
                id="import-comment"
                type="text"
                value={importComment}
                onChange={(e) => dispatch({ type: 'SET_IMPORT_COMMENT', payload: e.target.value })}
                placeholder="Optional comment for the audit log..."
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-1 focus:ring-teal-500 focus:border-teal-500 bg-white text-sm"
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-emerald-700 font-medium text-sm flex items-center">
                <CheckCircle2 className="w-4 h-4 mr-2" />
                All validations passed {overrides.size > 0 && `(${overrides.size} overridden)`}
              </span>
              <button
                onClick={handleSaveClick}
                className="flex items-center px-6 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium shadow-sm shadow-teal-600/20 transition-colors focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 text-sm"
              >
                <Save className="w-4 h-4 mr-2" />
                Save & Import
              </button>
            </div>
          </div>
        )}
      </div>
      )}

      {/* REJECT MODAL OVERLAY */}
      {isRejectMode && (
        <div className="absolute inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-200 border border-slate-200">
            <h3 className="text-lg font-bold text-slate-900 mb-2">Reject Record <span className="font-mono text-base font-normal text-slate-500">#{record.id}</span></h3>
            <p className="text-sm text-slate-500 mb-4">
              Please provide a reason for rejecting this record. This will be logged in the system.
            </p>
            <textarea
              ref={textareaRef}
              className="w-full h-32 px-3 py-2 border border-slate-300 rounded-lg mb-4 focus:ring-1 focus:ring-red-500 focus:border-red-500 resize-none text-sm placeholder-slate-400"
              placeholder="Reason for rejection..."
              value={rejectReason}
              onChange={(e) => dispatch({ type: 'SET_REJECT_REASON', payload: e.target.value })}
            />
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => dispatch({ type: 'SET_REJECT_MODE', payload: false })}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 rounded-lg font-medium text-sm transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmReject}
                disabled={!rejectReason.trim()}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium text-sm shadow-sm transition-colors"
              >
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
